<template>
    <div :class="[classObject, `z-date--${currentTheme}`]">
        <div class="z-date__card" v-if="from">
            <span class="z-date__day" v-if="from.day">{{from.day}}</span>
            <div class="z-date__wrapper" v-if="from.month">
                <span class="z-date__other" v-if="from.month">{{from.month}}</span>
                <span class="z-date__other" v-if="from.month">{{from.year}}</span>
            </div>
        </div>
        <z-icon
            class="z-date__separator"
            :name="currentIcon"
            v-if="from !== null && to !== null"
        />
        <div class="z-date__card" v-if="to">
            <span class="z-date__day" v-if="to.day">{{ to.day }}</span>
            <div class="z-date__wrapper">
                <span class="z-date__other">{{ to.month }}</span>
                <span class="z-date__other">{{ to.year }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import {useCurrentIcon} from './composable/useCurrentIcon'
import {useFormattedDate} from './composable/useFormattedDate'
import {useGetter} from '@/composable/useGetter'

const props = defineProps({
    separator: {
        type: String,
        default: 'dash',
        validator: prop => ['dash', 'dot', 'square'].includes(prop)
    },
    from: {
        type: [String, Number],
        required: true
    },
    to: {
        type: [String, Number]
    },
    size: {
        type: String,
        validator: prop => ['s', 'm', 'l'].includes(prop),
        default: 'l'
    },
    theme: {
        type: String,
        default: 'primary',
        validator: prop => ['primary', 'light', 'simple'].includes(prop)
    },
    mobileTheme: {
        type: String,
        validator: prop => ['primary', 'light', 'simple'].includes(prop)
    }
})

const { device } = useGetter(['device'])

const classObject = computed(() => {
    return [
        'z-date',
        `z-date--size-${props.size}`
    ]
})

const currentTheme = computed(() => {
    return props.mobileTheme && device.value === 'mobile' ? props.mobileTheme : props.theme
})

const {currentIcon} = useCurrentIcon(props.icon)
const { from, to } = useFormattedDate(props.from, props.to)
</script>

<style lang="scss">
.z-date {
    // color
    --font-color: var(--color-black-80);
    --font-color-secondary: var(--color-white);

    // colorFillIcon
    --icon-color: var(--color-black-80);
    --icon-color-secondary: var(--color-white);

    // simple theme
    --font-color-simple: var(--color-black-80);
    --icon-size: 1em;

    &--size-l {
        --font-size-day: 56px;
        --font-size-day-tablet: 42px;
        --font-size-day-mobile: 38px;
        --weigth-day: 400;
        --separator-spacer: 4px;
        --card-spacer: 8px;

        // month and year
        --font-size-other: 20px;
        --font-size-other-tablet: 18px;
        --font-size-other-mobile: 16px;
        --weight-other: 600;
        --line-height-other: 140%;

        // simple theme
        --simple-size: 20px;
        --simple-size-tablet: 18px;
        --simple-size-mobile: 16px;
        --simple-weight: 400;
    }

    &--size-m {
        --font-size-day: 50px;
        --font-size-day-tablet: 40px;
        --font-size-day-mobile: 30px;
        --weigth-day: 500;
        --separator-spacer: 4px;
        --card-spacer: 6px;

        // month and year
        --font-size-other: 20px;
        --font-size-other-tablet: 18px;
        --font-size-other-mobile: 16px;
        --weight-other: 600;
        --line-height-other: 140%;

        // simple theme
        --simple-size: 20px;
        --simple-size-tablet: 18px;
        --simple-size-mobile: 16px;
        --simple-weight: 400;
    }

    &--size-s {
        --font-size-day: 40px;
        --font-size-day-tablet: 32px;
        --font-size-day-mobile: 24px;
        --weigth-day: 500;
        --separator-spacer: 4px;
        --card-spacer: 4px;

        // month and year
        --font-size-other: 16px;
        --font-size-other-tablet: 16px;
        --font-size-other-mobile: 14px;
        --weight-other: 600;
        --line-height-other: 130%;

        // simple theme
        --simple-size: 18px;
        --simple-size-tablet: 16px;
        --simple-size-mobile: 14px;
        --simple-weight: 400;
    }
}

.z-date {
    $parent: &;
    display: flex;
    width: 100%;
    /* max-width: 177px; */
    align-items: center;
    gap: var(--separator-spacer);

    &__card {
        display: flex;
        align-items: center;
        gap: var(--card-spacer);
    }

    &__day {
        font-weight: var(--weigth-day);
        font-size: var(--font-size-day);
        line-height: 1;
        color: var(--font-color);

        @include breakpoint(tablet) {
            font-size: var(--font-size-day-tablet);
        }

        @include breakpoint(mobile) {
            font-size: var(--font-size-day-mobile);
        }
    }

    &__wrapper {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
    }

    &__other {
        font-weight: var(--weight-other);
        font-size: var(--font-size-other);
        line-height: var(--line-height-other);
        color: var(--font-color);

        @include breakpoint(tablet) {
            font-size: var(--font-size-other-tablet);
        }

        @include breakpoint(mobile) {
            font-size: var(--font-size-other-mobile);
        }
    }

    .z-icon {
        width: 32px;
        height: 32px;

        path,
        rect,
        circle {
            fill: var(--icon-color);
        }

        @include breakpoint(tablet) {
            width: 16px;
            height: 16px;
        }
    }

    &--light {
        .z-icon {
            g {
                rect,
                circle {
                    fill: var(--icon-color-secondary);
                }
            }
        }

        #{$parent}__day,
        #{$parent}__other {
            color: var(--font-color-secondary);
        }
    }

    &--simple {
        gap: 4px;

        #{$parent}__card {
            gap: 4px;
        }

        #{$parent}__day,
        #{$parent}__other {
            font-size: var(--simple-size);
            font-weight: var(--simple-weight);
            color: var(--font-color-simple);

            @include breakpoint(tablet) {
                font-size: var(--simple-size-tablet);
            }

            @include breakpoint(mobile) {
                font-size: var(--simple-size-mobile);
            }
        }

        #{$parent}__wrapper {
            display: inline-flex;
            flex-direction: row;
            gap: 4px;
        }

        #{$parent}__separator {
            width: var(--icon-size);
            height: var(--icon-size);
        }
    }
}
</style>
